import { Injectable, TemplateRef } from '@angular/core';
import { ApiService } from './api.service';
import { Store } from '../store';
import {
    catchError,
    delay,
    delayWhen,
    distinctUntilChanged,
    filter,
    map,
    repeat, sample,
    takeWhile, withLatestFrom
} from 'rxjs/operators';
import { Call, Device } from '@twilio/voice-sdk';
import { environment as env } from '../../environments/environment';
import {
    BehaviorSubject,
    Observable,
    of,
    ReplaySubject,
    Subject,
    Subscription,
    take,
    timer
} from 'rxjs';
import { defaultContactsParams, SearchParams } from '../data-types/search-params';
import {
    CallState,
    CountryPhoneCodesMap,
    defaultVoiceContactData,
    DeviceState,
    TwilioEvents,
    VoiceContactData,
    UserInputValues
} from '../data-types/voice-data';
import { format, fromUnixTime } from 'date-fns';
import { AuthHttpClient } from '../http/AuthHttpClient';
import { ApiAuthHttpClient } from '../http/ApiAuthHttpClient';
import { Socket } from 'ngx-socket-io';
import { ToastrService } from 'ngx-toastr';
import { LiveCallsRow, TwilioAgentModel, TwilioCallStats } from '../models/calls';
import { PhonePipe } from '../pipes/phone.pipe';
import { User } from '../shared/model/model';

// https://www.twilio.com/docs/voice/sdks/javascript/twiliocall#callsenddigitsdigits sendDigits
// Test internet connection https://networktest.twilio.com/

@Injectable({
    providedIn: 'root',
})
export class TwilioService {

    public inboundCall: Call | null = null;
    private onInboundDisconnectCallback = null; // calback function that will be executed right after call is disconected
    private onOutboundDisconnectCallback = null; // calback function that will be executed right after call is disconected
    public outboundCall: Call | null = null;

    private device: {
        [domain: string]: Array<{
            device: Device,
            readyForInbound: boolean,
            numberId: string,
            userId: string,
            heartBeat$?: Subscription
        }>
    } = {};
    private domainActiveNumbersIndex = 0;

    private isRecording$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    // private readonly clientPhone$: BehaviorSubject<null | string> = new BehaviorSubject(null); // this client active inbound phone number
    public readonly clientDomainIds$: BehaviorSubject<{ [domain: string]: Array<{ userId: string, numberId: string }> }> = new BehaviorSubject({}); // this client active inbound phone number

    private phoneList$: ReplaySubject<Array<any>> = new ReplaySubject(1);
    private contactsList$: Subject<Array<VoiceContactData>> = new Subject();
    private delayContactList$: Subscription = null;
    private httpContactList$: Subscription = null;
    readonly activeTwilioAgents$ = new BehaviorSubject<any[]>([]); // active twilio agent
    readonly activeDeviceDomain$ = new BehaviorSubject<string | null>(null); //
    private numbersForActiveDomain: Array<{ phone: string, private: boolean, main: boolean }> = [];

    private onLineContact$: BehaviorSubject<VoiceContactData | null> = new BehaviorSubject(null);
    public readonly onLineContact$$ = this.onLineContact$.asObservable();
    private onLinePhoneNr$: BehaviorSubject<string | null> = new BehaviorSubject(null);
    public readonly onLinePhoneNr$$ = this.onLinePhoneNr$.asObservable();
    private onLinePhoneLocation$: BehaviorSubject<string | null> = new BehaviorSubject(null);
    public readonly onLinePhoneLocation$$ = this.onLinePhoneLocation$.asObservable();
    private serviceEvents$: BehaviorSubject<TwilioEvents | null> = new BehaviorSubject(null); // some random events related to the service
    public readonly serviceEvents$$ = this.serviceEvents$.asObservable();

    private apiBase = env.apiPath + 'voice/';
    private twilioApiBase = env.apiPath + 'twilio/';
    // ONLY_DEV this only for dev.
    // private apiBase = 'http://localhost:3000/' + 'voice/';

    private callDuration$: BehaviorSubject<number> = new BehaviorSubject(0);
    private callDurationFomatted$: BehaviorSubject<string> = new BehaviorSubject('00:00');
    private durationTimer$;
    public callDuration$$ = this.callDuration$.asObservable();
    public callDurationFormatted$$ = this.callDurationFomatted$.asObservable();

    private phoneUKGeo$: ReplaySubject<Array<{ code: string; location: string }>>;
    public readonly deviceState$: ReplaySubject<DeviceState> = new ReplaySubject(1);
    public readonly callSubject$: ReplaySubject<string> = new ReplaySubject(1);
    public readonly onHoldCall$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    readonly disabled$: BehaviorSubject<boolean> = new BehaviorSubject(true);

    public readonly fullWindowControllerTmpl$: ReplaySubject<TemplateRef<any> | null> = new ReplaySubject<TemplateRef<any> | null>(1);
    public readonly barStyleTmpl$: ReplaySubject<TemplateRef<any> | null> = new ReplaySubject<TemplateRef<any> | null>(1);
    public readonly floatingTmpl$: ReplaySubject<TemplateRef<any> | null> = new ReplaySubject<TemplateRef<any> | null>(1);
    private twilioSocket: Socket; // this is meant to only to control the call
    private voiceSocket: Socket; // this is meant to only to control the call
    private _callState: CallState = CallState.IDLE;


    constructor(
        private apiService: ApiService,
        private store: Store,
        private http: ApiAuthHttpClient,
        private onlyAuthHttp: AuthHttpClient,
        private toastr: ToastrService
    ) {
        let user: User;

        this.callDuration$$.subscribe((sec) => {
            const secs = fromUnixTime(sec);
            if (sec > 3600) {
                // more then an hour
                this.callDurationFomatted$.next(format(secs, 'hh:mm:ss'));
            } else {
                this.callDurationFomatted$.next(format(secs, 'mm:ss'));
            }
        });

        this.voiceSocket = new Socket({ url: `${env.socketPath}voice`, options: { withCredentials: true } }); // twilio call namespace
        this.voiceSocket.on('connect', () => {
            this.voiceSocket
                .fromEvent<TwilioAgentModel>('agent-status')
                .pipe(
                    filter((item: TwilioAgentModel) => user && item.id !== user.id), // filtering my self
                    withLatestFrom(this.phoneList$)
                )
                .subscribe(([agentModel, phoneList]) => {
                    const agentIndex = phoneList.findIndex(item => item.id === agentModel.id && item.phone === agentModel.phone);
                    if (agentIndex) {
                        phoneList[agentIndex] = agentModel;
                        this.phoneList$.next(phoneList);
                    }
            });
        });

        // refreshing/ reloading user in case phone details has been changed
        this.store.select<User>('user')
            .pipe(
                filter((newUser: any) => newUser),
                distinctUntilChanged()
            )
            .subscribe(newUser => {
                if (user) {
                    const userTwNumberMap = user.twNumbers.reduce((acc, curr) => {
                        acc[curr.id] = curr;
                        return acc;
                    }, {});

                    const isTheSame = newUser.twNumbers.every(
                        (twNumber) =>
                            userTwNumberMap[twNumber.id]
                            && userTwNumberMap[twNumber.id].twilioNrId === twNumber.twilioNrId
                            && userTwNumberMap[twNumber.id].isPrivate === twNumber.isPrivate
                    );
                    if (!isTheSame || user.twNumbers.length !== newUser.twNumbers.length) {
                        this.disabled = true;
                        setTimeout(() => {
                            this.disabled = false;
                            setTimeout(() => this.initTwilio(user), 2000);
                        }, 1000);
                    }
                }
                user = JSON.parse(JSON.stringify(newUser));
            });

        this.onLinePhoneNr$$.subscribe((phoneNr) => {
            if (phoneNr) {
                const countryCode = this.getNumberCountry(phoneNr.replace('+', ''));
                if (countryCode) {
                    if (countryCode.countryCode === 'GB') {
                        this.getNumberUkLocation(phoneNr.replace('+', '').replace(countryCode.phoneCode, '')).subscribe(
                            (ukLocation) => {
                                if (ukLocation) {
                                    this.onLinePhoneLocation$.next(
                                        `${ukLocation.location}, ${countryCode.countryCode}`
                                    );
                                } else {
                                    this.onLinePhoneLocation$.next(
                                        `${countryCode.country}, ${countryCode.countryCode}`
                                    );
                                }
                            }
                        );
                    } else {
                        this.onLinePhoneLocation$.next(`${countryCode.country}, ${countryCode.countryCode}`);
                    }
                } else {
                    this.onLinePhoneLocation$.next('¯\\_(ツ)_/¯');
                }
            } else {
                this.onLinePhoneLocation$.next(null);
            }
        });

        this.deviceState$.subscribe((state: DeviceState) => {
            switch (state) {
                case DeviceState.INBOUND:
                case DeviceState.OUTGOING:
                    break;
                case DeviceState.INCOMING:
                case DeviceState.OUTBOUND:
                    const callSid = this.outboundCall
                        ? this.outboundCall.parameters.CallSid
                        : this.inboundCall.parameters.CallSid;

                    this.twilioSocket = new Socket({ url: `${env.socketPath}twilio`, options: { withCredentials: true } }); // twilio call namespace
                    this.twilioSocket.on('connect', () => {
                        this.twilioSocket.emit('join', { conf: this.confName });
                        this.twilioSocket
                            .fromEvent('call-status')
                            .subscribe((callStatus: { status: CallState; data?: any }) => {
                                if (callStatus.data && callSid === callStatus.data) {
                                    this._callState = callStatus.status;
                                }
                            });
                        this.twilioSocket
                            .fromEvent('conf-status')
                            .subscribe((callStatus: { status: CallState; data?: any }) => {
                                console.log(callStatus);
                                this._callState = callStatus.status;
                                if (callStatus.status === CallState.IDLE && this.isInboundCallRinging) {
                                    this.hangUpInboundCall();
                                    this.toastr.info('Customer hang up!');
                                }
                            });
                    });
                    break;
                case DeviceState.IDLE:
                    if (this.twilioSocket) {
                        this.twilioSocket.disconnect();
                        this.twilioSocket = null;
                    }
                    this._callState = CallState.IDLE;
                    break;
            }
        });
    }

    public initTwilio(user) {
        this.phoneList.pipe(take(1)).subscribe((twilioPhoneAgents) => {
            for (const twilioAgent of twilioPhoneAgents) {
                if (twilioAgent.id === user.id &&
                    (this.activeTwilioAgents.length === 0 ||
                     this.activeTwilioAgents.every(item => item.numberId !== twilioAgent.numberId))
                ) {
                    const clientDomains = this.clientDomainIds$.value;
                    if (!clientDomains[twilioAgent.domainId]) {
                        clientDomains[twilioAgent.domainId] = [];
                    }
                    clientDomains[twilioAgent.domainId].push({
                        userId: twilioAgent.id,
                        numberId: twilioAgent.numberId
                    });
                    this.clientDomainIds$.next(clientDomains);
                    const agents = this.activeTwilioAgents;
                    agents.push(twilioAgent);
                    this.activeTwilioAgents = agents;
                }
            }

            if (Object.keys(this.clientDomainIds$.value).length === 0) {
                this.disabled = true;
            } else {
                // domain$ = this.store.select<string>('domain');
                this.getTwilioTokens().then(() => {
                    this.disabled = false;
                    this.readyForInboundCalls();
                });
            }
        });
        this.disabled = false;
    }

    private initTwilioAgent(): void {
        let delayMs = 0;
        const delayTimer = () => timer(delayMs);
        of(this.onlyAuthHttp.get(this.twilioApiBase + 'agents'))
            .pipe(
                delayWhen(delayTimer),
                map((data: any) => data),
                repeat(),
                takeWhile(() => this.disabled$.value === false)
            )
            .subscribe((res: any) => {
                delayMs = 20 * 1000;
                res.subscribe((result) => this.updateTwilioAgentsList(result));
            });

        this.phoneList$
            .pipe(
                sample(this.store.select<string>('domain').pipe(distinctUntilChanged())),
                takeWhile(() => this.disabled$.value === false)
            )
            .subscribe((result) => {
                this.updateNumbersForDomain();
                this.updateTwilioAgentsList(result);
            });
    }

    private updateTwilioAgentsList(result) {
        if (result.length === 0) {
            this.disabled$.next(true);
        }
        this.phoneList$.next(result);
    }

    private updateNumbersForDomain() {
        const activeDomain = this.store.selectForLocal<string>('domain');
        const domainNumbers = this.activeTwilioAgents$.value.filter(item => item.domainId === activeDomain);
        this.numbersForActiveDomain = domainNumbers.map(item => ({
            phone: item.phone,
            private: item.isPrivate,
            main: item.default
        }));
    }

    get numbersForDomain(): Array<{ phone: string, private: boolean, main: boolean }> {
        return this.numbersForActiveDomain;
    }

    set activeTwilioAgents(twilioAgent: any | null) {
        this.activeTwilioAgents$.next(twilioAgent);
        this.updateNumbersForDomain();
    }

    get activeTwilioAgents(): any {
        return this.activeTwilioAgents$.value;
    }

    get activeTwilioDomain(): any {
        return this.activeDeviceDomain$.value;
    }

    get confName(): string {
        return this.outboundCall
            ? this.outboundCall.customParameters.get('conf')
            : this.inboundCall.customParameters.get('conf');
    }

    get currentTwilioAgent(): any[] | undefined {
        const domainId = this.store.selectForLocal('domain');
        return this.activeTwilioAgents$.value.filter(item => item.domainId === domainId);
    }

    set disabled(value: boolean) {
        this.disabled$.next(value);
        if (value === false) {
            this.initTwilioAgent();
        }
        if (value === true) {
            this.destroyDevices();
            this.clientDomainIds$.next({});
            this.activeTwilioAgents$.next([]);
            this.cleanService();
            /*this.clientPhone$.next(null);*/
        }
    }

    get disabled(): boolean {
        return this.disabled$.value;
    }

    get isRecording(): any {
        return this.isRecording$.value;
    }

    getAvailableHours(userId, day?: number) {
        return this.http.get(`${this.twilioApiBase}agent/${userId}/availability${day ? '/' + day : ''}`);
    }

    updateAvailableHours(availableHours, userId) {
        return this.http.patch(`${this.twilioApiBase}agent/${userId}/availability`, availableHours);
    }

    updateUserNumbers(numbers: Array<{id: string, isPrivate: boolean}>, userId) {
        return this.http.patch(`${this.twilioApiBase}agent/${userId}/numbers`, numbers);
    }

    pauseRecording() {
        const callSid = this.outboundCall ? this.outboundCall.parameters.CallSid : this.inboundCall.parameters.CallSid;
        this.http
            .patch(`${this.twilioApiBase}recording/${callSid}/pause`, {})
            .subscribe(() => this.isRecording$.next(false));
    }

    resumeRecording() {
        const callSid = this.outboundCall ? this.outboundCall.parameters.CallSid : this.inboundCall.parameters.CallSid;
        this.http
            .patch(`${this.twilioApiBase}recording/${callSid}/resume`, {})
            .subscribe(() => this.isRecording$.next(true));
    }

    getContacts(params: SearchParams, customPhone?: string): void {
        this.contactsList$.next([]);

        if (this.delayContactList$) {
            this.delayContactList$.unsubscribe();
        }
        if (this.httpContactList$) {
            this.httpContactList$.unsubscribe();
        }
        this.delayContactList$ = of(this.http.post(this.apiBase + 'contacts', { params }))
            .pipe(delay(350)) // delay until we hope next character will come in, before we do search
            .subscribe((search: Observable<any>) => {
                this.httpContactList$ = search.subscribe((result) => {
                    const list = result.data;
                    if (customPhone) {
                        list.unshift({
                            ...defaultVoiceContactData,
                            contactFirstname: 'Unknown',
                            contactMobile: customPhone,
                        } as VoiceContactData);
                    }
                    this.contactsList$.next(list);
                });
            });
    }

    searchContacts(phoneNr: string): Observable<any> {
        return this.onlyAuthHttp
            .post(this.apiBase + 'contacts', {
                params: {
                    ...defaultContactsParams,
                    ...{ searchText: phoneNr },
                } as SearchParams,
            })
            .pipe(map((data: any) => data.data[0]));
    }

    getContact(contactId: string): Observable<any> {
        return this.onlyAuthHttp.get(this.apiBase + `contacts/${contactId}`).pipe(map((data: any) => data.data[0]));
    }

    getNotes(callSid: string): Observable<any> {
        return this.onlyAuthHttp.get(this.apiBase + `notes/${callSid}`);
    }

    getCallStatistics(): Observable<TwilioCallStats> {
        return this.onlyAuthHttp.get<TwilioCallStats>(this.apiBase + `stats`);
    }

    getLiveCallsRows(): Observable<Array<LiveCallsRow>> {
        return this.onlyAuthHttp
            .get<Array<LiveCallsRow>>(this.apiBase + `live/calls`)
            .pipe(map(data => {
                return data.map((item: any) => {
                    return {
                        ...item,
                        createdAt: new Date(item.createdAt),
                        data: JSON.parse(item.data),
                        log: JSON.parse(item.log) as Array<string>,
                    } as LiveCallsRow;
                });
            }));
    }

    get contactsList(): Subject<Array<VoiceContactData>> {
        return this.contactsList$;
    }

    get phoneList(): Subject<Array<any>> {
        return this.phoneList$;
    }

    get isDeviceReady(): boolean {
        const devicesByDomain = Object.values(this.device);
        return devicesByDomain.length > 0
            && devicesByDomain.every(devices => devices.length > 0 && devices.every(device => !!device?.device));
    }

    async getTwilioTokens(): Promise<void> {
        if (Object.keys(this.clientDomainIds$.value).length === 0) {
            return Promise.reject('No client ID`s is set');
        }

        return new Promise<void>((resolve, reject) => {
            this.destroyDevices();
            Promise
                .all(Object.entries(this.clientDomainIds$.value).reduce((acc, domainPhones) => {
                    for (const domainPhone of domainPhones[1]) {
                        acc.push(this.getTwilioToken(domainPhone.userId, domainPhone.numberId, domainPhones[0]));
                    }
                    return acc;
                }, []))
                .then((result) => {
                    const numbersStr = result.reduce((acc, curr) =>
                        `${acc}${acc ? '<br>' : ''}${curr.deviceI + 1}(${curr.domainId}).${new PhonePipe().transform(curr.phone)}: ${curr.availableForInbound ? 'inbound & outbound' : 'outbound only'}`,
                        ''
                    );
                    const ifOnlyOutbound = result.every(item => !item.availableForInbound);
                    const numLines = result.length + (ifOnlyOutbound ? 1 : 0);
                    const statusToast = this.toastr.show(
                        numbersStr + (ifOnlyOutbound ? '<br>Click here to adjust your availability.' : ''),
                        null,
                        {
                            enableHtml: true,
                            timeOut: 0,
                            toastClass: `phone-toast ${ numLines > 2 ? '--' + numLines + '-lines' : '' } ${ifOnlyOutbound ? '--warning' : ''} ngx-toastr`
                    });
                    statusToast.onTap
                        .pipe(take(1))
                        .subscribe(() => this.serviceEvents$.next(TwilioEvents.SHOW_TWILIO_SETTINGS));
                    resolve();
                });
        });
    }

    async getTwilioToken(userId: string, numberId: string, domainId: string): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            this.onlyAuthHttp
                .get(this.twilioApiBase + `token/${userId}/${numberId}`)
                .pipe(
                    catchError((error) => {
                        this.disabled = true;
                        return of(false);
                    }),
                    takeWhile((res) => res !== false)
                )
                .subscribe((resp: any) => {
                    const user = this.store.selectForLocal('user');
                    if (!this.device[domainId]) {
                        this.device[domainId] = [];
                    }
                    const lastDeviceI = this.device[domainId].length;
                    this.device[domainId].push({
                        device: new Device(resp.token, {
                            codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
                            logLevel: env.isDev ? 1 : 5,
                            sounds: {
                                incoming: user.inboundRingtone ?? '/assets/inbound_tracks/office_phone.mp3',
                                /*outgoing: '/assets/outbound_call.mp3',*/
                            },
                            allowIncomingWhileBusy: true
                        }),
                        numberId,
                        userId,
                        readyForInbound: false
                    });

                    this.device[domainId][lastDeviceI].device.on(Device.EventName.Error, (twilioError, call) => {
                        this.apiService.handleError(twilioError);
                    });

                    this.initInboundEvents(domainId, resp.inboundDND, lastDeviceI);
                    resolve({
                        ...resp,
                        domainId,
                        deviceI: lastDeviceI
                    });
                });
        });
    }

    /* inbound call methods */
    public destroyDevices() {
        if (Object.values(this.device).length > 0) {
            for (const devices of Object.values(this.device)) {
                for (const device of devices) {
                    device.device.destroy();
                    device.readyForInbound = false;
                    this.onlyAuthHttp.get(this.twilioApiBase + `nopulse/${device.userId}/${device.numberId}`).subscribe();
                    device.heartBeat$?.unsubscribe();
                }
            }
            this.device = {};
        }
    }

    /* inbound call methods */
    public inboundCallsDnd(dnd: boolean) {
        if (Object.values(this.device).length > 0) {
            for (const devices of Object.values(this.device)) {
                for (const device of devices) {
                    device.readyForInbound = !dnd;
                    /// agent/:agentId/inbound/:dnd
                    this.onlyAuthHttp.patch(this.twilioApiBase + `agent/${device.userId}/inbound/${dnd}`, {}).subscribe();
                }
            }
        }
    }

    private initInboundEvents(domainId: string, iniDnd: boolean, deviceI: number): void {
        const clientDevice = this.device[domainId][deviceI];
        clientDevice.device.on(Device.EventName.Destroyed, (device) => {
            // console.log('destroyed', device);
            this.voiceSocket.emit('agent-offline', { userId: clientDevice.userId });
        });
        clientDevice.device.on(Device.EventName.Unregistered, (device) => {
            // console.log('Unregistered', device);
        });

        clientDevice.device.on(Device.EventName.TokenWillExpire, (device) => {
            this.onlyAuthHttp
                .get(this.twilioApiBase + `token/${clientDevice.userId}/${clientDevice.numberId}`)
                .subscribe((resp: any) => {
                    device.setup(resp.token);
                });
        });
        clientDevice.device.on(Device.EventName.Registered, (device) => {
            // was ready
            clientDevice.readyForInbound = !iniDnd;

            if (clientDevice.heartBeat$) {
                clientDevice.heartBeat$.unsubscribe();
            }

            clientDevice.heartBeat$ = of(true)
                .pipe(repeat({ delay: (count) => timer(5 * 1000)}))
                .subscribe(() =>
                    this.voiceSocket.emit('agent-online', {
                        userId: clientDevice.userId,
                        numberId: clientDevice.numberId
                    })
                );

            this.onlyAuthHttp // resets server status if there is something from before
                .get(this.twilioApiBase + `deviceStatus/${clientDevice.device.isBusy}/${clientDevice.userId}/${clientDevice.numberId}`)
                .subscribe();
        });

        clientDevice.device.on(Device.EventName.Incoming, (call) => {
            const fromPhone = call.parameters.From[0] === '+' ? call.parameters.From : call.customParameters.get('to');

            if (this.inboundCall || this.outboundCall) { // already on the call
                const inboundCallToastr = this.toastr.success(
                    `<div class="reminder-title">Incomming call from!!</div>
                     <div class="reminder-time">Click to pick up call from ${fromPhone}</div>`,
                    null,
                    {
                        enableHtml: true,
                        timeOut: 0,
                        toastClass: 'phone-toast ngx-toastr'
                    }
                );

                inboundCallToastr.onTap
                    .pipe(take(1))
                    .subscribe(() => {
                        if (this.inboundCall) {
                            this.onInboundDisconnectCallback = () => {
                                this.initiateInboundCall(call, domainId, fromPhone, clientDevice);
                                this.onInboundDisconnectCallback = null;
                                this.pickUpInboundCall();
                            };
                        }
                        if (this.outboundCall) {
                            this.onOutboundDisconnectCallback = () => {
                                this.initiateInboundCall(call, domainId, fromPhone, clientDevice);
                                this.onOutboundDisconnectCallback = null;
                                this.pickUpInboundCall();
                            };
                        }
                        this.hangUpCall();
                    });

            } else {
                this.initiateInboundCall(call, domainId, fromPhone, clientDevice);
            }
        });
    }

    private initiateInboundCall(call, domainId, fromPhone, clientDevice) {
        this.inboundCall = call;

        this.activeDeviceDomain$.next(domainId);

        this.onLinePhoneNr$.next(fromPhone);
        this.onLineContact$.next(null);
        this.deviceState$.next(DeviceState.INCOMING);

        const index = Object.keys(UserInputValues).indexOf(call.customParameters.get('digit'));
        this.callSubject$.next(
            index !== -1
                ? Object.values(UserInputValues)[index].toString()
                : Object.values(UserInputValues)[0].toString()
        );

        this.inboundCall.on('cancel', () => {
            this.inboundCall = null;
            this.cleanService();
        });
        this.inboundCall.on('error', () => {
            this.inboundCall = null;
            this.cleanService();
        });
        this.inboundCall.on('disconnect', () => {
            this.inboundCall = null;
            this.onlyAuthHttp // on hangup
                .get(this.twilioApiBase + `deviceStatus/${clientDevice.device.isBusy}/${clientDevice.userId}/${clientDevice.numberId}`)
                .subscribe();
            this.cleanService();
            this.onInboundDisconnectCallback && this.onInboundDisconnectCallback();
        });
        this.inboundCall.on('accept', (some) => {
            this.deviceState$.next(DeviceState.INBOUND);
            this.onlyAuthHttp // on pickup
                .get(this.twilioApiBase + `deviceStatus/${clientDevice.device.isBusy}/${this.clientDomainIds$.value[domainId][0].userId}/${clientDevice.numberId}`)
                .subscribe();
            this.onlyAuthHttp
                .patch(`${this.twilioApiBase}recording/${this.inboundCall.parameters.CallSid}/start`, {})
                .subscribe(() => this.isRecording$.next(true));
        });
    }

    sendDigits(digit: string) {
        if (this.inboundCall) {
            this.inboundCall.sendDigits(digit);
        }
        if (this.outboundCall) {
            this.outboundCall.sendDigits(digit);
        }
    }

    readyForInboundCalls(): void {
        Object
            .values(this.device)
            .forEach(devices => {
                for (const device of devices) {
                    if (!device.readyForInbound) {
                        device.device.register();
                    }
                }
            });
    }

    get isReadyForInbound(): boolean {
        const activeDomain = this.store.selectForLocal<string>('domain');
        return !!this.device[activeDomain]?.length && this.device[activeDomain].every(item => item.readyForInbound);
    }

    get isInboundCall(): boolean {
        return !!this.inboundCall;
    }

    get isInboundCallRinging(): boolean {
        return this.inboundCall?.status() === 'pending';
    }

    get isInboundCallGoingOn(): boolean {
        return this.inboundCall?.status() === 'open';
    }

    pickUpInboundCall(): void {
        this.inboundCall.accept();
        this.durationTimer$ = timer(0, 1000).subscribe((val) => this.callDuration$.next(val));
    }

    rejectInboundCall(): void {
        this.inboundCall.reject();
        this.inboundCall = null;
        this.cleanService();
    }

    ignoreInboundCall(): void {
        this.inboundCall.ignore();
        this.inboundCall = null;
        this.cleanService();
    }

    hangUpInboundCall(): void {
        if (this.isInboundCallRinging) {
            this.inboundCall.reject();
        } else {
            this.inboundCall.disconnect();
        }
        this.inboundCall = null;
        this.cleanService();
    }

    hangUpCall(): void {
        if (this.isInboundCallRinging) {
            this.hangUpInboundCall();
        } else if (this.isOutboundCallRinging || this.isOutboundCallGoingOn) {
            this.hangUpOutboundCall();
        } else {
            this.onlyAuthHttp
                .patch(this.twilioApiBase + `conf/${this.inboundCall.customParameters.get('conf')}/hangup`, {
                    ...Object.fromEntries(this.inboundCall.customParameters.entries()),
                })
                .subscribe();

            this.inboundCall = null;
            this.cleanService();
        }
    }

    toggleMuteInboundCall(): void {
        this.inboundCall.mute(!this.inboundCall.isMuted());
    }

    get callUniqueId(): string {
        return this.inboundCall ? this.inboundCall.customParameters.get('conf') : this.outboundCall.customParameters.get('conf');
    }

    get callCustomerNumber(): string | null { // return customer number if it's an outbound or inbound number
        const confName = this.inboundCall ? this.inboundCall.customParameters.get('conf') : this.outboundCall.customParameters.get('conf');
        //  'private' | 'inbound' - inbound f.e. private-1212:+4441414
        //  'customer' - outbound to customer f.e. customer-695:+4741172569
        //  'admin' - internal call admin to admin
        if (['private', 'inbound', 'customer'].indexOf(confName.split('-')[0]) !== -1) { // definitely to customer call
            return confName.split(':')[1];
        }

        return null;
    }

    get isCallMuted(): boolean {
        if (this.isOutboundCallGoingOn) {
            return this.outboundCall.isMuted();
        } else if (this.isInboundCallGoingOn) {
            return this.inboundCall.isMuted();
        } else {
            return false;
        }
    }

    get isLineBusy(): boolean {
        return (
            this.isInboundCallGoingOn ||
            this.isInboundCallRinging ||
            this.isOutboundCallGoingOn ||
            this.isOutboundCallRinging
        );
    }

    get callState(): CallState {
        return this._callState;
    }

    get CallState(): typeof CallState {
        return CallState;
    }

    set contact(contact: VoiceContactData) {
        this.onLineContact$.next(contact);
    }

    get numberToUser(): number {
        return this.domainActiveNumbersIndex;
    }

    set numberToUser(index) {
        this.domainActiveNumbersIndex = index;
    }

    /* outbound call methods */
    startOutboundCall(to: string, contact?: VoiceContactData, cbRefSid?: string): void {
        if (this.activeDeviceDomain$.value) {
            console.log("There is an outbouding call at the moment");
            return;
        }
        const To = to.replace(/^\+44|^44|^0| |-/g, '+44');
        this.doOutboundCall(To, this.numberToUser, 'customer-' + (parseInt((Math.random() * 1000).toString(10), 10)) + ':' +  To, cbRefSid);

        this.onLinePhoneNr$.next(To);
        if (contact) {
            this.onLineContact$.next(contact);
        } else {
            this.onLineContact$.next(null);
        }
    }

    startOutboundCallClient(cliendIdent: string, phoneToShow: string, contact: VoiceContactData): void {
        if (this.activeDeviceDomain$.value) {
            console.log("There is an outbouding call at the moment");
            return;
        }
        this.doOutboundCall('client:' + cliendIdent, this.numberToUser, 'admin-' + (parseInt((Math.random() * 1000).toString(10), 10)) + ':' +  cliendIdent);

        this.onLinePhoneNr$.next(phoneToShow);
        if (contact) {
            this.onLineContact$.next(contact);
        } else {
            this.onLineContact$.next(null);
        }
    }

    /* outbound call methods */
    private doOutboundCall(To: any, deviceI: number, conf?: string, cbRefSid?: string): void {
        // const To = '+44' + to.replace(/^\+44|^44|^0| |-/g, '');
        const activeDomain = this.store.selectForLocal<string>('domain');
        this.activeDeviceDomain$.next(activeDomain);

        this.device[activeDomain][deviceI].device
            .connect({ // userId + '-' + numberId
                params: {
                    To,
                    ...(conf ? { conf } : {}),
                    ...(cbRefSid ? { cbRefSid } : {})
                }
            })
            .then((call) => {
                this.outboundCall = call;
                this.deviceState$.next(DeviceState.OUTGOING);

                this.outboundCall.on('accept', (res) => {
                    this.durationTimer$ = timer(0, 1000).subscribe((val) => this.callDuration$.next(val));
                    this.deviceState$.next(DeviceState.OUTBOUND);
                    this.onlyAuthHttp
                        .patch(`${this.twilioApiBase}recording/${this.outboundCall.parameters.CallSid}/start`, {})
                        .subscribe(() => this.isRecording$.next(true));
                });

                this.outboundCall.on('cancel', () => {
                    this.outboundCall = null;
                    this.cleanService();
                });

                this.outboundCall.on('disconnect', () => {
                    this.outboundCall = null;
                    this.cleanService();
                    this.onOutboundDisconnectCallback && this.onOutboundDisconnectCallback();
                });

                this.outboundCall.on('error', () => {
                    this.outboundCall = null;
                    this.cleanService();
                });
            });
    }

    get isOutboundCall(): boolean {
        return !!this.outboundCall;
    }

    get isOutboundCallGoingOn(): boolean {
        // dummy number where woman talks :) 443458734678345 or contact 34678345
        return this.outboundCall?.status() === 'open';
    }

    get isOutboundCallRinging(): boolean {
        return this.outboundCall?.status() === 'ringing';
    }

    hangUpOutboundCall(): void {
        if (this.isOutboundCallRinging) {
            this.outboundCall.reject();
        } else {
            this.outboundCall.disconnect();
        }
        this.outboundCall = null;
        this.cleanService();
    }

    rejectOutboundCall(): void {
        this.outboundCall.reject();
        this.outboundCall = null;
        this.cleanService();
    }

    toggleMuteOutboundCall(): void {
        this.outboundCall.mute(!this.outboundCall.isMuted());
    }

    getNumberUkLocation(phoneNr: string): Observable<{ code: string; location: string } | null> {
        if (!this.phoneUKGeo$) {
            this.phoneUKGeo$ = new ReplaySubject(1);
            // I’ve added both endpoints, /voice/geo will return an array of all,
            // /voice/geo/0113 will return {code: 0113, location: ‘Leeds’}
            this.onlyAuthHttp.get(this.apiBase + 'geo').subscribe((data: any) => this.phoneUKGeo$.next(data));
        }

        return this.phoneUKGeo$.pipe(
            map((data: Array<{ code: string; location: string }>) => {
                let prefixLength = 5;
                let prefix = phoneNr.substring(0, prefixLength);
                while (prefix !== '') {
                    const foundLocation = data.find((item) => item.code === '0' + prefix);
                    if (foundLocation) {
                        return foundLocation;
                    }
                    prefixLength--;
                    prefix = phoneNr.substring(0, prefixLength);
                }
                return null;
            })
        );
    }

    getCallRecordingsForNr(
        page: number,
        limit: number,
        params: {
            dir?: string;
            accType?: string;
            searchWord?: string;
            date?: string;
            ascOrDesc?: 'ASC' | 'DESC';
            sort?: string;
            source?: string;
        },
        phoneNr?: Array<string>
    ): Observable<any> {
        let queryString = '';
        if (phoneNr) {
            const phoneNrs = new URLSearchParams();
            phoneNr.forEach(item => phoneNrs.append('phoneNr', item));
            queryString = `?${phoneNrs.toString()}`;
        }

        return this.http.post(this.apiBase + `recordings/${page}/${limit}${queryString}`, params);
    }

    getRecordMediaFilePage(sid): Observable<any> {
        return this.http.get(this.apiBase + 'mediaFile/' + sid);
    }

    getCallHistoryForNr(
        page: number,
        limit: number,
        params?: {
            dir?: string;
            accType?: string;
            searchWord?: string;
            date?: string;
            ascOrDesc?: 'ASC' | 'DESC';
            sort?: string;
        },
        phoneNr?: Array<string>
    ): Observable<any> {
        let queryString = '';
        if (phoneNr) {
            const phoneNrs = new URLSearchParams();
            phoneNr.forEach(item => phoneNrs.append('phoneNr', item));
            queryString = `?${phoneNrs.toString()}`;
        }

        return this.http.post(this.apiBase + `log/${page}/${limit}${queryString}`, params);
    }

    getCallHistoryByCId(
        page: number,
        limit: number,
        customerId: string,
        dir?: string,
        withPhoneNr?: string
    ): Observable<any> {
        return this.http.get(this.apiBase + `log/${page}/${limit}/${customerId}/cid`, {
            params: { ...(dir ? { dir } : {}), ...(withPhoneNr ? { withPhoneNr } : {}) },
        });
    }

    getUserTwilioNumbers(userId?: string): Observable<Array<any>> {
        return this.onlyAuthHttp.get<Array<any>>(this.apiBase + `numbers/${userId}`);
    }

    getAvailableTwilioNumbers(brand: string): Observable<Array<any>> {
        return this.onlyAuthHttp.get<Array<any>>(this.twilioApiBase + `numbers/${brand}/`);
    }

    getNumberCountry(phoneNr: string): any | null {
        let digitCount = 4;
        let maybeCode = phoneNr.substring(0, digitCount);
        while (maybeCode !== '') {
            if (CountryPhoneCodesMap[maybeCode]) {
                return {
                    country: CountryPhoneCodesMap[maybeCode][0],
                    countryCode: CountryPhoneCodesMap[maybeCode][2],
                    phoneCode: CountryPhoneCodesMap[maybeCode][1],
                };
            }
            digitCount--;
            maybeCode = phoneNr.substring(0, digitCount);
        }

        return null;
    }

    toggleMuteCall(): void {
        if (this.isOutboundCallGoingOn) {
            this.toggleMuteOutboundCall();
        } else {
            this.toggleMuteInboundCall();
        }
    }

   /* get canCallBeHold(): boolean {
        if (this.outboundCall) {
            const to = this.outboundCall.customParameters.get('To');
            if (!(to.indexOf('client:') === 0 && this.outboundCall.customParameters.get('conf'))) { // if it's internal call
                return false;
            }
        }

        return true;
    }*/


    toggleHoldConf(): void {
        if (this.outboundCall) {
            this.onlyAuthHttp
                .patch(this.twilioApiBase + `conf/${this.outboundCall.parameters.CallSid}/hold`, {
                    callSid: this.outboundCall
                        ? this.outboundCall.parameters.CallSid
                        : this.inboundCall.parameters.CallSid// // This call sid initiator
                })
                .subscribe((isOnHold: boolean) => this.onHoldCall$.next(isOnHold));
        } else {
            this.onlyAuthHttp
                .patch(this.twilioApiBase + `conf/${this.inboundCall.customParameters.get('callSid')}/hold`, {
                    callSid: this.outboundCall
                        ? this.outboundCall.parameters.CallSid
                        : this.inboundCall.parameters.CallSid// // This call sid initiator
                })
                .subscribe((isOnHold: boolean) => this.onHoldCall$.next(isOnHold));
        }
    }

    connectAnotherAgentToCall(agent) {
        const httpParams = [];
        const inOutCall = this.outboundCall ?? this.inboundCall;
        inOutCall.customParameters.forEach((value, key) =>
            httpParams.push(`${decodeURI(key)}=${encodeURIComponent(value)}`)
        );
        if (this.outboundCall) {
            httpParams.push(`${decodeURI('callSid')}=${encodeURIComponent(inOutCall.parameters.CallSid)}`);
        }


        this.onlyAuthHttp
            .patch(
                this.twilioApiBase +
                    `conf/${inOutCall.customParameters.get('conf')}/redirect?${httpParams.join('&')}`,
                {
                    callSid: inOutCall.parameters.CallSid,
                    toAgentId: agent.id,
                }
            )
            .subscribe();
    }

    private cleanService() {
        this.onLinePhoneNr$.next(null);
        this.callDuration$.next(0);
        if (this.durationTimer$) {
            this.durationTimer$.unsubscribe();
        }
        this.durationTimer$ = null;
        this.onLineContact$.next(null);
        this.onHoldCall$.next(false);
        this.callSubject$.next('');
        this.deviceState$.next(DeviceState.IDLE);
        // const activeDomain = this.activeDeviceDomain$.value;
        this.activeDeviceDomain$.next(null);
    }

    set fullWindowController(tmpl: TemplateRef<any> | null) {
        this.fullWindowControllerTmpl$.next(tmpl);
    }

    set barStyleController(tmpl: TemplateRef<any> | null) {
        this.barStyleTmpl$.next(tmpl);
    }

    set floatStyleController(tmpl: TemplateRef<any> | null) {
        this.floatingTmpl$.next(tmpl);
    }
}
